// Serviço do domínio patients — carteira do médico.
import { createClient } from '@/lib/supabase/client'
import type { OrigemRegistro, Paciente, SituacaoPaciente } from '@/lib/types'
import { registrarAuditoria } from '@/services/identity'
import { registrarEvento } from '@/services/timeline'

export interface PacienteComClinicas extends Paciente {
  clinica_ids: string[]
}

export interface FiltrosPacientes {
  busca?: string
  situacao?: SituacaoPaciente | 'todas'
  origem?: OrigemRegistro | 'todas'
  clinicaId?: string | 'todas'
}

const CAMPOS =
  'id, workspace_id, nome, cpf, data_nasc, genero, email, telefone, observacoes, origem, situacao, proxima_acao, created_at, paciente_clinicas(clinica_id)'

function mapRow(row: Record<string, unknown>): PacienteComClinicas {
  const vinculos = (row.paciente_clinicas as { clinica_id: string }[] | null) ?? []
  const { paciente_clinicas: _drop, ...resto } = row
  void _drop
  return { ...(resto as unknown as Paciente), clinica_ids: vinculos.map(v => v.clinica_id) }
}

export async function listPacientes(workspaceId: string, filtros: FiltrosPacientes = {}): Promise<PacienteComClinicas[]> {
  const supabase = createClient()
  let q = supabase
    .from('pacientes')
    .select(CAMPOS)
    .eq('workspace_id', workspaceId)
    .order('nome')
    .limit(500)

  if (filtros.situacao && filtros.situacao !== 'todas') q = q.eq('situacao', filtros.situacao)
  if (filtros.origem && filtros.origem !== 'todas') q = q.eq('origem', filtros.origem)
  if (filtros.busca?.trim()) {
    const b = filtros.busca.trim()
    q = q.or(`nome.ilike.%${b}%,cpf.ilike.%${b.replace(/\D/g, '') || b}%`)
  }

  const { data, error } = await q
  if (error) throw error
  let rows = (data ?? []).map(r => mapRow(r as Record<string, unknown>))
  if (filtros.clinicaId && filtros.clinicaId !== 'todas') {
    rows = rows.filter(p => p.clinica_ids.includes(filtros.clinicaId as string))
  }
  return rows
}

export async function getPaciente(workspaceId: string, id: string): Promise<PacienteComClinicas | null> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('pacientes')
    .select(CAMPOS)
    .eq('workspace_id', workspaceId)
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  return data ? mapRow(data as Record<string, unknown>) : null
}

export interface DadosPaciente {
  nome: string
  cpf?: string | null
  data_nasc?: string | null
  genero?: string | null
  email?: string | null
  telefone?: string | null
  observacoes?: string | null
  proxima_acao?: string | null
  situacao?: SituacaoPaciente
}

export async function criarPaciente(params: {
  workspaceId: string
  dados: DadosPaciente
  clinicaIds: string[]
  origem?: OrigemRegistro
}): Promise<string> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('pacientes')
    .insert({
      workspace_id: params.workspaceId,
      ...params.dados,
      cpf: params.dados.cpf?.replace(/\D/g, '') || null,
      origem: params.origem ?? 'manual',
    })
    .select('id')
    .single()
  if (error) throw error
  const pacienteId = (data as { id: string }).id

  if (params.clinicaIds.length > 0) {
    await supabase.from('paciente_clinicas').insert(
      params.clinicaIds.map(clinicaId => ({
        paciente_id: pacienteId,
        clinica_id: clinicaId,
        workspace_id: params.workspaceId,
      }))
    )
  }

  await registrarEvento({
    workspaceId: params.workspaceId,
    pacienteId,
    tipo: 'paciente_criado',
    titulo: 'Paciente cadastrado',
    descricao: `Origem: ${params.origem ?? 'manual'}`,
    eventoDominio: 'patient.created',
  })
  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'paciente_criado',
    entidade: 'pacientes',
    registroId: pacienteId,
  })
  return pacienteId
}

export async function atualizarPaciente(params: {
  workspaceId: string
  id: string
  dados: Partial<DadosPaciente>
  clinicaIds?: string[]
}): Promise<void> {
  const supabase = createClient()
  const patch: Record<string, unknown> = { ...params.dados }
  if (patch.cpf !== undefined && patch.cpf !== null) {
    patch.cpf = String(patch.cpf).replace(/\D/g, '') || null
  }
  const { error } = await supabase
    .from('pacientes')
    .update(patch)
    .eq('id', params.id)
    .eq('workspace_id', params.workspaceId)
  if (error) throw error

  if (params.clinicaIds) {
    // sincroniza vínculos: remove os que saíram, insere os novos
    const { data: atuais } = await supabase
      .from('paciente_clinicas')
      .select('clinica_id')
      .eq('paciente_id', params.id)
    const atuaisIds = ((atuais ?? []) as { clinica_id: string }[]).map(v => v.clinica_id)
    const remover = atuaisIds.filter(id => !params.clinicaIds!.includes(id))
    const inserir = params.clinicaIds.filter(id => !atuaisIds.includes(id))
    if (remover.length > 0) {
      await supabase
        .from('paciente_clinicas')
        .delete()
        .eq('paciente_id', params.id)
        .in('clinica_id', remover)
    }
    if (inserir.length > 0) {
      await supabase.from('paciente_clinicas').insert(
        inserir.map(clinicaId => ({
          paciente_id: params.id,
          clinica_id: clinicaId,
          workspace_id: params.workspaceId,
        }))
      )
    }
  }

  await registrarAuditoria({
    workspaceId: params.workspaceId,
    acao: 'paciente_atualizado',
    entidade: 'pacientes',
    registroId: params.id,
    dados: patch,
  })
}
